import axios, { AxiosResponse } from 'axios'
import { axiosInstance } from '../App'
import { FilterListDTO, LoadConfigDTO, SortingFieldDTO } from '../dto/LoadConfigDTO'
import {
  UrgencyDetailPageDTO,
  UrgencyDetailParamsDTO,
  UrgencyDetailRowDTO,
  UrgencyExportFormat,
  UrgencyHourDTO,
  UrgencyLongestWaitDTO,
  UrgencyLongestWaitsRequestDTO,
  UrgencyOptionsDTO,
  UrgencyPriorityRowDTO,
  UrgencySummaryDTO,
  UrgencySummaryExportRequestDTO,
  UrgencySummaryRequestDTO,
  UrgencyWhereRowDTO,
} from '../dto/urgencySummary/UrgencySummaryDTO'
import { ApiErrorResponse, parseApiError } from './apiError'

/** Prefijo de los endpoints securizados: se define una sola vez para esta capa de servicios. */
export const SECURED_API_BASE = '/api/secured'
const URGENCY_SUMMARY_API = `${SECURED_API_BASE}/urgency-summary`

/** Códigos de error del backend que la pantalla distingue. */
export const URGENCY_ACCESS_DENIED = 'ACCESS_DENIED'

/** Filtros del detalle sin la paginación ni el orden: lo que viaja en el `state` del router. */
export type UrgencyDetailFilters = Omit<UrgencyDetailParamsDTO, 'loadConfigDTO' | 'filtersText' | 'format'>

/** Fila del detalle con la clave que usa la tabla (`dataKey`). */
export type UrgencyDetailTableRow = UrgencyDetailRowDTO & { rowKey: number }

/** Lo que `RemoteTable` le pasa a `getItems` y a `onExport` (orden y filtros de la tabla). */
export interface UrgencyTableParams {
  multiSortMeta?: unknown
  filterDTO?: unknown
}

/** Error del backend ya traducido al contrato `ErrorResponse`: el componente nunca ve el error crudo de axios. */
export class UrgencyApiError extends Error {
  readonly status: number
  readonly errorCode: string

  constructor(apiError: ApiErrorResponse) {
    super(apiError.message)
    this.name = 'UrgencyApiError'
    this.status = apiError.status
    this.errorCode = apiError.errorCode
  }
}

type UnknownRecord = Record<string, unknown>

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null
}

/** Con `responseType: 'blob'` el cuerpo del error también llega como Blob: se lee para que `parseApiError` vea el JSON. */
async function unwrapBlobError(error: unknown): Promise<void> {
  if (!axios.isAxiosError(error) || !error.response || !(error.response.data instanceof Blob)) return
  try {
    error.response.data = JSON.parse(await error.response.data.text()) as unknown
  } catch {
    error.response.data = null
  }
}

/** Único punto donde se parsea el error de axios (contrato `ErrorResponse`). */
async function toUrgencyApiError(error: unknown): Promise<UrgencyApiError> {
  await unwrapBlobError(error)
  return new UrgencyApiError(parseApiError(error))
}

/** True si el pedido se canceló con el `AbortController`: no es un error para mostrar. */
export function isRequestCanceled(error: unknown): boolean {
  return axios.isCancel(error)
}

/** True si el backend respondió que el perfil no tiene acceso (403 `ACCESS_DENIED`). */
export function isAccessDenied(error: unknown): boolean {
  return error instanceof UrgencyApiError && error.errorCode === URGENCY_ACCESS_DENIED
}

/**
 * Mensaje para mostrar: el del backend si es un error de negocio (4xx: rango inválido, sin permiso), que ya viene
 * filtrado y dice qué hacer; si no (red, servidor), el texto genérico de la acción que falló.
 */
export function errorMessageOf(error: unknown, fallback: string): string {
  return error instanceof UrgencyApiError && error.status >= 400 && error.status < 500 ? error.message : fallback
}

async function request<TResponse>(
  path: string,
  body: unknown,
  signal?: AbortSignal,
  responseType?: 'blob',
): Promise<AxiosResponse<TResponse>> {
  try {
    return await axiosInstance.post<TResponse>(`${URGENCY_SUMMARY_API}${path}`, body, { signal, responseType })
  } catch (error: unknown) {
    if (axios.isCancel(error)) throw error
    throw await toUrgencyApiError(error)
  }
}

async function post<TResponse>(path: string, body: unknown, signal?: AbortSignal): Promise<TResponse> {
  return (await request<TResponse>(path, body, signal)).data
}

export function getUrgencyOptions(signal?: AbortSignal): Promise<UrgencyOptionsDTO> {
  return post<UrgencyOptionsDTO>('/options', {}, signal)
}

export function getUrgencySummary(summaryRequest: UrgencySummaryRequestDTO, signal?: AbortSignal): Promise<UrgencySummaryDTO> {
  return post<UrgencySummaryDTO>('/summary', summaryRequest, signal)
}

/** Las 24 horas de llegada con llegadas, atendidos, retiros y espera: los mismos filtros que `/summary`. */
export function getUrgencyHourly(summaryRequest: UrgencySummaryRequestDTO, signal?: AbortSignal): Promise<UrgencyHourDTO[]> {
  return post<UrgencyHourDTO[]>('/hourly', summaryRequest, signal)
}

/** La espera por código de triaje con su objetivo provisorio (ignora `priorityId`). */
export function getUrgencyPriorities(summaryRequest: UrgencySummaryRequestDTO, signal?: AbortSignal): Promise<UrgencyPriorityRowDTO[]> {
  return post<UrgencyPriorityRowDTO[]>('/priorities', summaryRequest, signal)
}

/** «Dónde y cuándo se espera»: una fila por guardia con llegadas, con su nivel y hasta tres posibles causas. */
export function getUrgencyWhere(summaryRequest: UrgencySummaryRequestDTO, signal?: AbortSignal): Promise<UrgencyWhereRowDTO[]> {
  return post<UrgencyWhereRowDTO[]>('/where', summaryRequest, signal)
}

/** Las `limit` atenciones (de 1 a 20) con más espera del período, de mayor a menor, sin datos del paciente. */
export function getUrgencyLongestWaits(
  summaryRequest: UrgencySummaryRequestDTO,
  limit: number,
  signal?: AbortSignal,
): Promise<UrgencyLongestWaitDTO[]> {
  const body: UrgencyLongestWaitsRequestDTO = { ...summaryRequest, limit }
  return post<UrgencyLongestWaitDTO[]>('/longest-waits', body, signal)
}

function toFilterList(value: unknown): FilterListDTO | null {
  if (!isRecord(value)) return null
  const filters = Array.isArray(value.filters) ? value.filters : []
  const filterLists = Array.isArray(value.filterLists) ? value.filterLists : []
  if (filters.length === 0 && filterLists.length === 0) return null
  // Mismo FilterDTO que arma RemoteTable y que ya acepta el LoadConfigDTO del resto de los listados
  return value as unknown as FilterListDTO
}

function toSortingList(value: unknown): SortingFieldDTO[] {
  if (!Array.isArray(value)) return []
  return value.flatMap((item): SortingFieldDTO[] => {
    if (!isRecord(item) || typeof item.fieldName !== 'string') return []
    return [{
      fieldName: item.fieldName,
      ascOrder: typeof item.ascOrder === 'boolean' ? item.ascOrder : true,
      sqlFormula: null,
      compositeSortOrder: null,
    }]
  })
}

/** Paginación, orden y filtros de `RemoteTable` en el formato del backend (`LoadConfigDTO`). */
function buildLoadConfig(offSet: number | null, pageSize: number | null, tableParams?: unknown): LoadConfigDTO {
  const table: UrgencyTableParams = isRecord(tableParams) ? tableParams : {}
  return {
    offSet,
    pageSize,
    sortingFieldInfoList: toSortingList(table.multiSortMeta),
    filters: toFilterList(table.filterDTO),
  }
}

/** Arma el body de `/detail` y `/detail/export` a partir de los filtros fijos y de lo que pide `RemoteTable`. */
export function buildUrgencyDetailParams(
  base: UrgencyDetailFilters,
  offSet: number | null,
  pageSize: number | null,
  tableParams?: unknown,
  filtersText?: string,
): UrgencyDetailParamsDTO {
  return { ...base, loadConfigDTO: buildLoadConfig(offSet, pageSize, tableParams), ...(filtersText ? { filtersText } : {}) }
}

/**
 * Una página del detalle para `RemoteTable`, que espera un array y no recibe el total:
 * se mapea `{ rows, total }` a `rows` y el total se informa por `onTotal`.
 */
export async function getUrgencyDetailRows(
  base: UrgencyDetailFilters,
  offSet: number,
  pageSize: number,
  tableParams?: unknown,
  onTotal?: (total: number) => void,
  signal?: AbortSignal,
): Promise<UrgencyDetailTableRow[]> {
  const page = await post<UrgencyDetailPageDTO>('/detail', buildUrgencyDetailParams(base, offSet, pageSize, tableParams), signal)
  onTotal?.(page.total)
  // La consulta del detalle no trae un id por fila: la tabla necesita una clave única y estable por posición
  return page.rows.map((row, index) => ({ ...row, rowKey: offSet + index }))
}

/**
 * Exporta el detalle completo con los mismos filtros y orden de la tabla (sin paginar). Devuelve la respuesta entera:
 * el botón de exportar de `RemoteTable` toma el nombre del archivo del `Content-Disposition`.
 */
export function exportUrgencyDetail(
  base: UrgencyDetailFilters,
  tableParams?: unknown,
  filtersText?: string,
  format: UrgencyExportFormat = 'xlsx',
): Promise<AxiosResponse<Blob>> {
  const params = buildUrgencyDetailParams(base, null, null, tableParams, filtersText)
  return request<Blob>('/detail/export', { ...params, format }, undefined, 'blob')
}

/**
 * Exporta el resumen (indicadores, evolución diaria y las cuatro agrupaciones) a PDF o Excel con los filtros de la última
 * consulta. Devuelve la respuesta entera: `ExportMenu` toma el nombre del archivo del `Content-Disposition`. El error
 * sale tal como lo da axios (no `UrgencyApiError`): `ExportMenu` lo lee con `exportErrorMessage`.
 */
export function exportUrgencySummary(
  summaryRequest: UrgencySummaryRequestDTO,
  format: UrgencyExportFormat,
  filtersText?: string,
): Promise<AxiosResponse<Blob>> {
  const body: UrgencySummaryExportRequestDTO = { ...summaryRequest, format, ...(filtersText ? { filtersText } : {}) }
  return axiosInstance.post<Blob>(`${URGENCY_SUMMARY_API}/export`, body, { responseType: 'blob' })
}
